import { isFunction, isArray } from '@helpers/utils.js'
import useRoute from '@composables/useRoute.js'
import { create } from './axiosClient.js'
import { authStore } from '@stores'
import { HttpError } from '@errors'

const { isLoggedIn, clearUser } = authStore()

export const TOKEN_EXPIRED = 11005

export default function createClient(configs = []) {
  let client = {
    httpClient: create(),
  }

  const updateConfigs = isArray(configs) ? configs : Array.from([configs])

  updateConfigs.forEach((updateConfigFn) => {
    if (!isFunction(updateConfigFn)) {
      throw new TypeError('Argument must be a function')
    }
    updateConfigFn(client)
  })

  async function get(uri, config = {}) {
    try {
      return await client.httpClient.get(uri, config)
    } catch (err) {
      if (err.response) {
        const retry = await __handleUnauthorise(err)

        if (retry) return get(uri, config)

        throw new HttpError(err.response)
      }
    }
  }

  async function post(uri, postData, config = {}) {
    try {
      return await client.httpClient.post(uri, postData, config)
    } catch (err) {
      if (err.response) {
        await __handleUnauthorise(err)
        throw new HttpError(err.response)
      }
    }
  }

  async function put(uri, postData = null, config = {}) {
    try {
      return await client.httpClient.put(uri, postData, config)
    } catch (err) {
      if (client.httpClient.isCancel(err)) {
        throw err
      } else if (err.response) {
        await __handleUnauthorise(err)
        throw new HttpError(err.response)
      }
    }
  }

  async function del(uri, postData = null, config) {
    try {
      return await client.httpClient.delete(uri, { ...config, data: postData })
    } catch (err) {
      if (err.response) {
        await __handleUnauthorise(err)
        throw new HttpError(err.response)
      }
    }
  }

  async function uploadFile(uri, postData = {}, onUploadProgress) {
    try {
      return await client.httpClient.post(uri, postData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        onUploadProgress,
      })
    } catch (err) {
      if (err.response) {
        await __handleUnauthorise(err)
        throw new HttpError(err.response)
      }
    }
  }

  async function __handleUnauthorise(error) {
    if (!isLoggedIn.value) return false

    if (parseInt(error.response.status) === 401) {
      if (error.response.data?.error?.error_ref === TOKEN_EXPIRED) {
        // Wait to let other requests finish
        await new Promise((resolve) => setTimeout(resolve, 500))

        clearUser()

        const { routeMeta, push } = useRoute()
        if (routeMeta.value?.requiresAuth) {
          push({ path: '/' })
          return false
        }

        return true
      }
    }

    return false
  }

  function setClient(c) {
    client.httpClient = c
  }

  function getClient() {
    return client.httpClient
  }

  return {
    uploadFile,
    getClient,
    setClient,
    post,
    get,
    put,
    del,
  }
}
